/**
 * When an event starts, and which of several live events is the one on now - shared by Find's group path
 * (FindProximityContext / userGroups), its solo path (useLiveRsvpEvent) and Maps (isInPresenceWindow).
 *
 * 2026-10-16 audit: all three did `new Date(event_datetime ?? event_date)`. A bare 'YYYY-MM-DD' parses as UTC
 * midnight - 5 PM the day before in Los Angeles - so with the 3 h lead-in a date-only event opened Find at 2 PM the
 * afternoon before. And with two live events the same day, the earliest start won all night.
 */

/** events.timezone's default (and venues.timezone's): the server's rule when a row names none. */
export const DEFAULT_EVENT_TIMEZONE = 'America/Los_Angeles';

type EventLike = Record<string, unknown> | null | undefined;

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function venueTimezone(venues: unknown): string | null {
  const row = Array.isArray(venues) ? venues[0] : venues;
  return row && typeof row === 'object' ? str((row as { timezone?: unknown }).timezone) : null;
}

const offsetFormatters = new Map<string, Intl.DateTimeFormat>();

/** The zone's offset from UTC at `utcMs`, in ms (positive east of Greenwich). NaN for an unknown zone. */
function zoneOffsetMs(utcMs: number, timeZone: string): number {
  let fmt = offsetFormatters.get(timeZone);
  if (!fmt) {
    try {
      fmt = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return NaN;
    }
    offsetFormatters.set(timeZone, fmt);
  }
  const parts: Record<string, number> = {};
  for (const p of fmt.formatToParts(new Date(utcMs))) {
    if (p.type !== 'literal') parts[p.type] = Number(p.value);
  }
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour % 24,
    parts.minute,
    parts.second,
  );
  return asUtc - Math.floor(utcMs / 1000) * 1000;
}

/** A wall-clock time in `timeZone` as epoch ms. Two passes, so a time just after a DST change lands right. */
export function zonedWallTimeToMs(
  y: number,
  mo: number,
  d: number,
  h: number,
  mi: number,
  s: number,
  timeZone: string,
): number {
  const wall = Date.UTC(y, mo - 1, d, h, mi, s);
  const first = wall - zoneOffsetMs(wall, timeZone);
  const offset = zoneOffsetMs(first, timeZone);
  return Number.isFinite(offset) ? wall - offset : NaN;
}

/**
 * When the event starts, as epoch ms; NaN when it has no usable date. event_datetime when the row has it;
 * otherwise event_date + event_time (00:00 when there is none) in the event's zone (events.timezone, else the
 * venue's, else the server default). Takes raw event rows and Find's group options (eventDate / eventTime /
 * eventTimezone / eventStartsAt) alike. A full timestamp in the date field (userGroups puts event_datetime there)
 * is read as the timestamp it is.
 */
export function getEventStartMs(event: EventLike): number {
  if (!event) return NaN;
  const datetime =
    str(event.eventStartsAt) ?? str(event.event_datetime) ?? str(event.eventDateTime);
  if (datetime) {
    const t = new Date(datetime).getTime();
    if (Number.isFinite(t)) return t;
  }
  const date = str(event.event_date) ?? str(event.eventDate) ?? str(event.date);
  if (!date) return NaN;
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!day) return new Date(date).getTime();
  const time = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(
    str(event.event_time) ?? str(event.eventTime) ?? '',
  );
  const zone =
    str(event.timezone) ??
    str(event.eventTimezone) ??
    venueTimezone(event.venues) ??
    DEFAULT_EVENT_TIMEZONE;
  const at = (tz: string) =>
    zonedWallTimeToMs(
      Number(day[1]),
      Number(day[2]),
      Number(day[3]),
      time ? Number(time[1]) : 0,
      time ? Number(time[2]) : 0,
      time?.[3] ? Number(time[3]) : 0,
      tz,
    );
  const ms = at(zone);
  return Number.isFinite(ms) ? ms : at(DEFAULT_EVENT_TIMEZONE); // a misspelt zone is not "no date"
}

/**
 * Of several events that are all live now, the one on: the one whose start most recently passed, else (none has
 * started - all are in their lead-in) the soonest to start. Ties go to the lower id, so the answer is the same
 * every time and the session key does not flip between renders. A festival and its afterparty: the festival until
 * the afterparty starts, then the afterparty. A day party and a night show: the night show once it starts.
 *
 * Not done (2026-10-16): preferring the event whose venue you are near. No self position exists before the
 * session runs - the session is what this picks - so there is nothing cheap to compare against.
 */
export function pickCurrentLiveEvent<T>(
  candidates: readonly T[],
  options: { startMs: (c: T) => number; id: (c: T) => string; now?: number },
): T | null {
  const now = options.now ?? Date.now();
  let best: T | null = null;
  let bestStart = NaN;
  let bestStarted = false;
  for (const c of candidates) {
    const start = options.startMs(c);
    if (!Number.isFinite(start)) continue;
    const started = start <= now;
    let better: boolean;
    if (best === null) better = true;
    else if (started !== bestStarted) better = started;
    else if (start !== bestStart) better = started ? start > bestStart : start < bestStart;
    else better = options.id(c) < options.id(best);
    if (better) {
      best = c;
      bestStart = start;
      bestStarted = started;
    }
  }
  return best;
}
